import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Filter, Download, ChevronLeft, ChevronRight, X, Image as ImageIcon } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { listCases, formatCaseId } from "@/lib/api";
import ScoreBadge from "@/components/ScoreBadge";
import StatusBadge from "@/components/StatusBadge";
import { SkeletonBlock } from "@/components/Skeletons";
import { downloadCasesCsv } from "@/lib/exports";

const PAGE_SIZE = 8;

export default function CaseHistory() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    from: "",
    to: "",
    minScore: 0,
    maxScore: 100,
    flaggedOnly: false,
  });
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  const fetchData = useCallback(() => {
    setRows(null);
    setErr(null);
    listCases({
      search: filters.search,
      from: filters.from || null,
      to: filters.to || null,
      minScore: Number(filters.minScore) || 0,
      maxScore: Number(filters.maxScore) || 100,
      flaggedOnly: filters.flaggedOnly,
    })
      .then((r) => setRows(r))
      .catch((e) => setErr(e.message || "Failed to load history"));
  }, [filters]);

  useEffect(() => {
    setPage(1);
    fetchData();
  }, [fetchData]);

  const paged = useMemo(() => {
    if (!rows) return [];
    const start = (page - 1) * PAGE_SIZE;
    return rows.slice(start, start + PAGE_SIZE);
  }, [rows, page]);
  const totalPages = rows ? Math.max(1, Math.ceil(rows.length / PAGE_SIZE)) : 1;

  const resetFilters = () =>
    setFilters({
      search: "",
      from: "",
      to: "",
      minScore: 0,
      maxScore: 100,
      flaggedOnly: false,
    });

  const handleCsv = () => {
    if (!rows || rows.length === 0) {
      toast.error("Nothing to export.");
      return;
    }
    downloadCasesCsv(rows, `yoobikwitus-history-${new Date().toISOString().slice(0, 10)}.csv`);
    toast.success("Export complete", { description: `${rows.length} cases exported.` });
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="history-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 mb-1">Archive</div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">Case history</h1>
          <p className="text-sm text-zinc-400 mt-2 max-w-2xl">
            All verifications on your desk. Filter by date, score, or flag status, then export
            the filtered view as CSV.
          </p>
        </div>
        <button
          onClick={handleCsv}
          data-testid="export-csv-button"
          className="inline-flex items-center gap-2 rounded-lg bg-white text-[#0A0A0B] font-semibold text-sm px-4 py-2.5 hover:bg-zinc-200 transition-colors"
        >
          <Download className="h-4 w-4" /> Export filtered (CSV)
        </button>
      </header>

      <section
        className="rounded-xl border border-[#272A30] bg-[#111318] p-4"
        data-testid="history-filters"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-4">
            <label className="block text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1">
              Search
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
              <input
                value={filters.search}
                onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                placeholder="Title, source, file name, case id…"
                data-testid="history-search-input"
                className="w-full rounded-lg bg-[#0A0A0B] border border-[#272A30] focus:border-zinc-500 focus:ring-1 focus:ring-zinc-500 outline-none pl-9 pr-3 py-2 text-sm placeholder:text-zinc-600"
              />
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="block text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1">
              From
            </label>
            <input
              type="date"
              value={filters.from}
              onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
              data-testid="history-from-input"
              className="w-full rounded-lg bg-[#0A0A0B] border border-[#272A30] focus:border-zinc-500 focus:ring-1 focus:ring-zinc-500 outline-none px-3 py-2 text-sm"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1">
              To
            </label>
            <input
              type="date"
              value={filters.to}
              onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
              data-testid="history-to-input"
              className="w-full rounded-lg bg-[#0A0A0B] border border-[#272A30] focus:border-zinc-500 focus:ring-1 focus:ring-zinc-500 outline-none px-3 py-2 text-sm"
            />
          </div>
          <div className="md:col-span-3">
            <label className="block text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1">
              Score range
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={0}
                max={100}
                value={filters.minScore}
                onChange={(e) => setFilters((f) => ({ ...f, minScore: e.target.value }))}
                data-testid="history-min-score"
                className="w-full rounded-lg bg-[#0A0A0B] border border-[#272A30] outline-none px-3 py-2 text-sm tabular-nums"
              />
              <span className="text-zinc-600">–</span>
              <input
                type="number"
                min={0}
                max={100}
                value={filters.maxScore}
                onChange={(e) => setFilters((f) => ({ ...f, maxScore: e.target.value }))}
                data-testid="history-max-score"
                className="w-full rounded-lg bg-[#0A0A0B] border border-[#272A30] outline-none px-3 py-2 text-sm tabular-nums"
              />
            </div>
          </div>
          <div className="md:col-span-1 flex md:justify-end">
            <label className="inline-flex items-center gap-2 text-xs text-zinc-300 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={filters.flaggedOnly}
                onChange={(e) => setFilters((f) => ({ ...f, flaggedOnly: e.target.checked }))}
                data-testid="history-flagged-only"
                className="h-4 w-4 rounded border-[#272A30] bg-[#0A0A0B] accent-red-500"
              />
              Flagged
            </label>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between">
          <div className="text-xs text-zinc-500 inline-flex items-center gap-1.5">
            <Filter className="h-3.5 w-3.5" />
            {rows ? `${rows.length} result${rows.length === 1 ? "" : "s"}` : "Filtering…"}
          </div>
          <button
            onClick={resetFilters}
            className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white"
            data-testid="history-reset"
          >
            <X className="h-3 w-3" /> Reset filters
          </button>
        </div>
      </section>

      {err && (
        <div
          role="alert"
          data-testid="history-error"
          className="rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 text-sm px-4 py-3"
        >
          {err}
        </div>
      )}

      <section className="rounded-xl border border-[#272A30] bg-[#111318] overflow-hidden">
        {!rows ? (
          <div className="p-5 space-y-3" data-testid="history-loading">
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonBlock key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center" data-testid="history-empty">
            <div className="text-zinc-300 font-medium">No cases match these filters</div>
            <p className="text-sm text-zinc-500 mt-1">Try widening the score or date range.</p>
            <button
              onClick={resetFilters}
              className="mt-4 inline-flex items-center gap-2 rounded-lg border border-[#272A30] px-3 py-1.5 text-xs uppercase tracking-wider text-zinc-300 hover:bg-[#1A1D24]"
            >
              Reset filters
            </button>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">
                <th className="px-5 py-3 text-left font-medium">Case</th>
                <th className="px-5 py-3 text-left font-medium">Date</th>
                <th className="px-5 py-3 text-left font-medium">Source</th>
                <th className="px-5 py-3 text-left font-medium">Status</th>
                <th className="px-5 py-3 text-left font-medium">Score</th>
                <th className="px-2 py-3" />
              </tr>
            </thead>
            <tbody>
              {paged.map((c) => {
                const displayId = formatCaseId(c.id);
                return (
                  <tr
                    key={c.id}
                    onClick={() => navigate(`/report/${c.id}`)}
                    data-testid={`history-row-${c.id}`}
                    className="border-t border-[#272A30]/60 hover:bg-[#14171D] cursor-pointer transition-colors"
                  >
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        {c.thumbnail ? (
                          <img
                            src={c.thumbnail}
                            alt=""
                            className="h-10 w-14 rounded-md object-cover ring-1 ring-[#272A30]"
                            loading="lazy"
                          />
                        ) : (
                          <div className="h-10 w-14 rounded-md bg-[#1A1D24] ring-1 ring-[#272A30] flex items-center justify-center flex-shrink-0">
                            <ImageIcon className="h-4 w-4 text-zinc-600" />
                          </div>
                        )}
                        <div>
                          <div className="text-sm font-medium">{c.title}</div>
                          <div className="text-[11px] text-zinc-500 mono">{displayId}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-sm text-zinc-400 whitespace-nowrap">
                      {new Date(c.submittedAt).toLocaleString()}
                    </td>
                    <td className="px-5 py-3 text-sm text-zinc-400 mono">{c.sourceDomain || "—"}</td>
                    <td className="px-5 py-3">
                      <StatusBadge status={c.flagged ? "flagged" : c.status} />
                    </td>
                    <td className="px-5 py-3">
                      <ScoreBadge score={c.score} />
                    </td>
                    <td className="px-2 py-3 text-zinc-500">
                      <ChevronRight className="h-4 w-4" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {rows && rows.length > 0 && (
          <div className="border-t border-[#272A30] px-5 py-3 flex items-center justify-between text-xs text-zinc-400">
            <span>
              Page <span className="text-zinc-200 tabular-nums">{page}</span> of{" "}
              <span className="text-zinc-200 tabular-nums">{totalPages}</span>
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                data-testid="history-prev-page"
                className="inline-flex items-center gap-1 rounded-md border border-[#272A30] px-2.5 py-1.5 text-xs hover:bg-[#1A1D24] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-3.5 w-3.5" /> Prev
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                data-testid="history-next-page"
                className="inline-flex items-center gap-1 rounded-md border border-[#272A30] px-2.5 py-1.5 text-xs hover:bg-[#1A1D24] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
